import React, { useEffect, useRef, ReactNode } from 'react';
// @ts-ignore
import 'bootstrap/js/dist/dropdown';
import Collapse from 'bootstrap/js/dist/collapse';
import cn from 'classnames';

export type NavItem = {
	href: string;
	label: string;
	target?: string;
	isActive?: boolean;
	isDisabled?: boolean;
	classNames?: string[];
	children?: NavItem[] | ReactNode;
};

export interface INavProps {
	className?: string;
	classNames?: string[];
	navItems?: NavItem[];
	justifyContent?: 'start' | 'center' | 'end';
	vertical?: boolean;
	type?: 'links' | 'tabs' | 'pills';
	dropdownEnabled?: boolean;
	dropdownAutoClose?: 'true' | 'false' | 'inside' | 'outside';
	collapseEnabled?: boolean;
	collapseOptions?: { [x: string]: any };
}

/**
 * Nav
 * @block
 * @requiresHooks
 */
const Nav: React.FC<INavProps> = ({
	navItems,
	children,
	className,
	classNames = [],
	justifyContent = 'start',
	vertical = false,
	type = 'links',
	dropdownEnabled = true,
	dropdownAutoClose,
	collapseEnabled = false,
}) => {
	const ref = useRef(null);

	useEffect(() => {
		if (!ref.current) {
			return;
		}

		if (dropdownEnabled === true) {
			const dropdownElementList: HTMLDivElement[] = [].slice.call(
				(ref.current! as HTMLDivElement).querySelectorAll(
					'.dropdown-toggle',
				),
			);
			dropdownElementList.map((dropdownToggleEl) => {
				dropdownToggleEl.addEventListener('show.bs.dropdown', () => {
					dropdownToggleEl
						.closest('.nav')
						?.classList.add('dropdown-visible');

					dropdownToggleEl.parentElement?.classList.add(
						'dropdown-open',
					);
				});

				dropdownToggleEl.addEventListener('hide.bs.dropdown', () => {
					dropdownToggleEl
						.closest('.nav')
						?.classList.remove('dropdown-visible');

					dropdownToggleEl.parentElement?.classList.remove(
						'dropdown-open',
					);
				});
			});
		}

		if (collapseEnabled === true) {
			const collapseElementList: HTMLDivElement[] = [].slice.call(
				(ref.current! as HTMLDivElement).querySelectorAll(
					'.collapse-toggle',
				),
			);
			collapseElementList.map((collapseToggleEl) => {
				const collapseEl =
					collapseToggleEl.nextElementSibling as HTMLDivElement;
				const collapse = new Collapse(collapseEl, {
					toggle: false,
				});

				collapseToggleEl.addEventListener('click', (e) => {
					e.preventDefault();
					e.stopPropagation();
					collapse.toggle();
				});

				collapseEl.addEventListener('show.bs.collapse', () => {
					collapseToggleEl.parentElement?.classList.add(
						'collapse-open',
					);
				});

				collapseEl.addEventListener('hide.bs.collapse', () => {
					collapseToggleEl.parentElement?.classList.remove(
						'collapse-open',
					);
				});
			});
		}
	}, []);

	// @ts-ignore
	const _navItems = navItems
		? ((Array.isArray(navItems)
				? navItems
				: Object.values(navItems)) as NavItem[])
		: null;

	return (
		<ul
			ref={ref}
			className={
				className ||
				cn(
					'nav',
					`justify-content-${justifyContent}`,
					vertical === true && 'flex-column',
					type === 'tabs' && 'nav-tabs',
					type === 'pills' && 'nav-pills',
					...classNames,
				)
			}
		>
			{_navItems &&
				_navItems.map((item, index) => {
					let linkArgs: any = {};
					const isDropdown =
						item.children &&
						!(
							Array.isArray(item.children) &&
							item.children.length < 1
						);
					const isMegaMenu =
						isDropdown && !Array.isArray(item.children);

					if (item.isActive === true) {
						linkArgs['aria-current'] = 'page';
					}

					if (item.isDisabled === true) {
						linkArgs['aria-disabled'] = 'true';
						linkArgs['tabIndex'] = '-1';
					}

					if (item.target) {
						linkArgs['target'] = item.target;
					}

					if (isDropdown) {
						linkArgs['aria-expanded'] = 'false';
						linkArgs['role'] = 'button';

						if (dropdownEnabled) {
							linkArgs['data-bs-toggle'] = 'dropdown';
							if (!dropdownAutoClose) {
								linkArgs['data-bs-auto-close'] = isMegaMenu
									? 'outside'
									: 'true';
							}
						}

						if (collapseEnabled) {
							linkArgs['data-bs-toggle'] = 'collapse';
						}
					}

					return (
						<li
							className={cn(
								'nav-item',
								isDropdown && 'dropdown',
								isMegaMenu && 'dropdown-mega-menu',
								...(item.classNames || []),
							)}
							key={`${item.href}-${index}`}
						>
							<a
								className={cn(
									'nav-link',
									item.isActive === true && 'active',
									item.isDisabled === true && 'disabled',
									dropdownEnabled &&
										isDropdown === true &&
										'dropdown-toggle',
									collapseEnabled &&
										isDropdown === true &&
										'collapse-toggle',
								)}
								href={item.href}
								{...linkArgs}
							>
								{item.label}
							</a>
							{isDropdown && (
								<ul
									className={cn(
										dropdownEnabled && 'dropdown-menu',
										collapseEnabled && 'collapse',
										isMegaMenu && 'mega-menu',
									)}
								>
									{isMegaMenu === true ? (
										<li>{item.children}</li>
									) : (
										(item.children! as NavItem[]).map(
											(item, childIndex) => (
												<li
													key={`${item.href}-${index}-${childIndex}`}
													className={cn(
														item.classNames || [],
													)}
												>
													<a
														className={cn(
															dropdownEnabled &&
																'dropdown-item',
															item.isActive ===
																true &&
																'active',
														)}
														href={item.href}
														target={
															item.target ||
															'_self'
														}
													>
														{item.label}
													</a>
												</li>
											),
										)
									)}
								</ul>
							)}
						</li>
					);
				})}
			{children}
		</ul>
	);
};

export default Nav;
